import { LocaleLink } from "~/lib/components/link";
import { ISectionProps } from "~/lib/types/article.type";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { ChevronRightIcon } from "@setel/web-ui";
import { useMemo, useState } from "react";
import cn from "classnames";
import { useIsMobileUser } from "~/lib/utils/store/useIsMobileUser";
import { getTranslationVariable } from "~/lib/utils/helper/getVariableTranslation";

export const Topic = ({ data }: { data: ISectionProps }) => {
  const VISIBLE_SUB_SECTION = 8; // display first 8 elements on mobile
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { isMobileUser } = useIsMobileUser();
  const { topic } = data;
  const [isShowAll, setIsShowAll] = useState(
    data.subSections.length <= VISIBLE_SUB_SECTION
  );

  const visibleSubSections = useMemo(() => {
    if (!isShowAll && isMobileUser) {
      return data.subSections.slice(0, VISIBLE_SUB_SECTION);
    }

    return data.subSections;
  }, [data.subSections, isShowAll, isMobileUser]);

  return (
    <div className="flex flex-col gap-3 border rounded-xl p-4 md:p-5 border-gray-200 transition duration-300">
      <div className="flex flex-row gap-3 items-center">
        {data.imageUrl && (
          <img
            width={44}
            height={44}
            src={data.imageUrl}
            alt={getTranslationVariable(data.topic, locale)}
          />
        )}
        <p
          className="md:text-lg text-[15px] leading-[21px]"
          data-testid="topic-title"
        >
          {getTranslationVariable(topic, locale)}
        </p>
      </div>
      <div>
        {visibleSubSections.map((subSection, idx) => (
          <LocaleLink
            key={subSection.value}
            to={
              subSection.isFeatureList
                ? `/features/${data.sectionId}/${subSection.value}`
                : `/topics/${data.sectionId}/${subSection.value}`
            }
            className={cn(
              "pt-4 flex flex-row justify-between border-t border-gray-200 hover:text-brand hover:underline",
              idx !== visibleSubSections.length - 1 ||
                (!isShowAll && isMobileUser)
                ? "pb-4"
                : ""
            )}
          >
            <div className="font-secondary font-normal text-[15px] leading-[21px]">
              {getTranslationVariable(subSection.label, locale)}
            </div>
            <ChevronRightIcon className="w-6 h-6 text-gray-light" />
          </LocaleLink>
        ))}
        {!isShowAll && isMobileUser && (
          <div
            className="pt-4 flex flex-row justify-between border-t border-gray-200 text-brand hover:underline font-secondary font-medium text-[14px] leading-[20px] cursor-pointer"
            onClick={() => setIsShowAll(true)}
          >
            {t("seeMoreTopics")}
          </div>
        )}
      </div>
    </div>
  );
};
